import * as React from 'react';
import { t } from '@reos/i18n';
import { FieldRow } from '@redhand/ui';
import { DomainApiError } from '../lib/api-error.js';
import { lookupAresCompany, type AresCompanyDto } from '../lib/billing-api.js';

const cs = { locale: 'cs' } as const;

/**
 * IcoAresField — SSOT pro "IČO + Načíst z ARES" (krok UXK2 doplnek bod 1:
 * stejna komponenta pro Novou fakturu i Noveho klienta, puvodne zila jen
 * v `klienti.tsx`). `onFound` dostane cely vysledek ARES - kazdy formular
 * si z nej vezme to sve (nazev/adresa/DIC).
 */
export interface IcoAresFieldProps {
  readonly id: string;
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly onFound: (company: AresCompanyDto) => void;
}

export function IcoAresField({ id, label, value, onChange, onFound }: IcoAresFieldProps): React.JSX.Element {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  async function lookup(): Promise<void> {
    const ico = value.trim();
    if (ico === '') return;
    setLoading(true);
    setError(null);
    try {
      const company = await lookupAresCompany(ico);
      if (company === null) {
        setError(t('webAdmin.formLayout.aresNotFound', cs));
        return;
      }
      onFound(company);
    } catch (e) {
      setError(e instanceof DomainApiError ? e.message : t('webAdmin.formLayout.aresLookupError', cs));
    } finally {
      setLoading(false);
    }
  }

  return (
    <FieldRow label={label} htmlFor={id}>
      <div className="flex flex-wrap gap-2">
        <input id={id} className="rh-input" style={{ flex: '1 1 120px', minWidth: 0 }} value={value} onChange={(e) => onChange(e.target.value)} data-testid={id} />
        <button
          type="button"
          className="rh-a2-btn"
          disabled={loading || value.trim() === ''}
          onClick={() => void lookup()}
          data-testid={`${id}-lookup`}
        >
          {loading ? '…' : t('webAdmin.formLayout.aresLookupButton', cs)}
        </button>
      </div>
      {error !== null && (
        <p role="alert" className="rh-a2-error" data-testid={`${id}-error`}>
          {error}
        </p>
      )}
    </FieldRow>
  );
}
